import classNames from "classnames";
import { find } from "lodash";
// eslint-disable-next-line no-restricted-imports
import { Router, useRouter } from "next/router";
import {
  ChangeEvent,
  FunctionComponent,
  useEffect,
  useRef,
  useState
} from "react";
import BillingInformationStep from "rivals/components/pages/Registration/Steps/BillingInformation";
import CompletePurchaseStep from "rivals/components/pages/Registration/Steps/CompletePurchase";
import GiftPurchaseStep from "rivals/components/pages/Registration/Steps/GiftPurchase";
import AddressVerificationModal from "rivals/components/shared/Form/BillingInformation/AddressVerificationModal";
import { INVALID_ADDRESS } from "rivals/redux/rails/constants";
import { initialState } from "rivals/services/billing/types";
import useObick from "rivals/services/billing/useObick";
import { CUSTOM_EVENTS } from "rivals/shared/analytics/customEvents";
import { GIFTING_PLAN_LENGTH_UNIT, PLANS } from "rivals/shared/constants";
import { Site } from "rivals/shared/interfaces/Site";
import { TaxAddress } from "rivals/shared/interfaces/User";
import commonStrings from "rivals/shared/strings/common";
import { setData } from "rivals/shared/utils/qa";
import { invalidGiftingInput, isValidEmail } from "rivals/shared/validations";
import { FormValues, OwnProps } from "./types";
import styles from "../Gifts.module.scss";
import strings from "../strings";

const { gifterEmailError, recipientEmailError, required } = strings;
const { payPalEmailError, payPalAccountError } = commonStrings;

const Gifts: FunctionComponent<OwnProps> = ({
  billingConstants,
  currentSite,
  giftPricing,
  giftSubscription,
  payPalAddress,
  payPalEmail,
  payPalError,
  payPalToken,
  pricingConstants,
  resetPayPal,
  sendGAEvent,
  submitting,
  updatePayPalError,
  giftPricingError
}) => {
  if (!billingConstants || !currentSite || !pricingConstants) {
    return null;
  }

  const headerClassNames = classNames(
    [styles.headerSection, styles.headline],
    "headerSection"
  );

  const dirty = useRef(false);

  const [formValues, setFormValues] = useState<FormValues>({
    errors: {
      gifterEmail: "",
      gifterName: "",
      personalNote: "",
      recipientEmail: "",
      recipientName: ""
    },
    gifterEmail: "",
    gifterName: "",
    recipientEmail: "",
    recipientName: ""
  });

  const [personalNote, setPersonalNote] = useState("");
  const [payPalEnabledEmail, setPayPalEnabledEmail] = useState("");
  const [submitClicked, setSubmitClicked] = useState(false);

  const { pathname } = useRouter();

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    dirty.current = true;
    setFormValues({
      ...formValues,
      [e.currentTarget.name]: e.currentTarget.value
    });
  };

  const handleInputBlur = (e: ChangeEvent<HTMLInputElement>): void => {
    e.preventDefault();
    const { name, value } = e.target;

    switch (name) {
      case "recipientName":
        setFormValues({
          ...formValues,
          errors: {
            ...formValues.errors,
            recipientName: value === "" ? required : ""
          }
        });
        break;
      case "recipientEmail":
        setFormValues({
          ...formValues,
          errors: {
            ...formValues.errors,
            recipientEmail: isValidEmail(value) ? "" : recipientEmailError
          }
        });
        break;
      case "gifterEmail":
        setFormValues({
          ...formValues,
          errors: {
            ...formValues.errors,
            gifterEmail: isValidEmail(value) ? "" : gifterEmailError
          }
        });
        if (payPalToken) {
          resetPayPal();
          updatePayPalError(payPalAccountError);
        }
        setPayPalEnabledEmail(value);
        break;
      case "gifterName":
        setFormValues({
          ...formValues,
          errors: {
            ...formValues.errors,
            gifterName: value === "" ? required : ""
          }
        });
        break;
      default:
        break;
    }
  };

  const handlePersonalNoteChange = (
    e: ChangeEvent<HTMLTextAreaElement>
  ): void => {
    dirty.current = true;
    setPersonalNote(e.target.value);
  };

  const handlePersonalNoteBlur = (
    e: ChangeEvent<HTMLTextAreaElement>
  ): void => {
    setFormValues({
      ...formValues,
      errors: {
        ...formValues.errors,
        personalNote: e.target.value === "" ? required : ""
      }
    });
  };

  const [selectedSite, setSelectedSite] = useState<Site>(currentSite);

  const handleSelectedSite = (value: Site): void => {
    dirty.current = true;
    setSelectedSite(value);
  };

  const handleSelectedSiteCleared = (): void => {
    dirty.current = false;
    setSelectedSite(currentSite);
  };

  const {
    autoRenewalOptInStates,
    debitTerms,
    obickSourceUrl
  } = billingConstants;

  const { giftPlans } = pricingConstants;

  const {
    billingFormProps,
    submit,
    isCheckingOut,
    isDebitAgreed,
    debitAgreedState,
    valid
  } = useObick({
    autoRenewalOptInStates,
    enableOptIn: false,
    formReady: !payPalToken,
    giftAction: giftSubscription,
    initialState,
    obickSourceUrl,
    payPalAddress,
    payPalToken
  });

  const {
    billingFormState: { city, state, street1, street2, zip },
    setIsAutoRenewAgreed,
    isAutoRenewAgreed,
    requiresAutoRenewalOptIn
  } = billingFormProps;

  const payPalLocation = {
    city: payPalAddress?.city || "",
    state: payPalAddress?.state || "",
    street1: payPalAddress?.street1 || "",
    street2: payPalAddress?.street2 || "",
    zip: payPalAddress?.zip || ""
  };
  const creditCardLocation = {
    city: city.value,
    state: state.value,
    street1: street1.value,
    street2: street2.value,
    zip: zip.value
  };

  const address =
    payPalToken && payPalAddress ? payPalLocation : creditCardLocation;

  const [taxAddress, setTaxAddress] = useState<TaxAddress>({ ...address });

  useEffect(() => {
    setTaxAddress({ ...address });
  }, [city.value, state.value, street2.value, zip, payPalAddress]);

  const defaultGiftPlan = find(
    giftPlans,
    plan =>
      plan.subscriptionLength === 1 && plan.subscriptionLengthUnit === "year"
  );
  const [selectedGiftPlan, setGiftPlan] = useState(
    defaultGiftPlan || giftPlans[0]
  );

  const handleFormAbandon = (): void => {
    if (dirty.current && !submitClicked) {
      sendGAEvent(CUSTOM_EVENTS.abandonForm("gift_subscription_form"));
    }
  };

  const getPayPalError = (): string => {
    if (payPalError) {
      return payPalError;
    }
    if (!formValues.gifterEmail || !isValidEmail(formValues.gifterEmail)) {
      return payPalEmailError;
    }
    return "";
  };

  useEffect(() => {
    window.addEventListener("beforeunload", handleFormAbandon);
    Router.events.on("routeChangeStart", handleFormAbandon);

    return (): void => {
      window.removeEventListener("beforeunload", handleFormAbandon);
      Router.events.off("routeChangeStart", handleFormAbandon);
    };
  }, [submitClicked]);

  const invalidInput = invalidGiftingInput(formValues) || !personalNote;

  const philterPage = "gift-purchase";

  const addressContainerRef = useRef<HTMLDivElement>(null);

  const [
    showAddressVerificationModal,
    setShowAddressVerificationModal
  ] = useState(false);

  const completePurchase = (): void => {
    const subscriptionPlan =
      selectedGiftPlan.subscriptionLengthUnit === GIFTING_PLAN_LENGTH_UNIT.YEAR
        ? PLANS.ANNUAL
        : PLANS.MONTHLY;
    setSubmitClicked(true);
    sendGAEvent(
      CUSTOM_EVENTS.createSubscription(
        "gift",
        subscriptionPlan,
        selectedSite.name
      )
    );
    submit({
      debitCardAgreement: isDebitAgreed,
      giver: {
        email: formValues.gifterEmail,
        name: formValues.gifterName
      },
      message: personalNote,
      recipient: {
        email: formValues.recipientEmail,
        name: formValues.recipientName
      },
      siteId: selectedSite.id,
      subscriptionLength: selectedGiftPlan.subscriptionLength,
      subscriptionLengthUnit: selectedGiftPlan.subscriptionLengthUnit
    });
  };

  const onPurchase = (): void => {
    // no address validation when selected payment method is paypal
    if (payPalToken) {
      completePurchase();
      return;
    }

    // TODO RVLS-8135: delete this block when RVLS-8006 is tested on Prod
    if (!pathname.includes("test")) {
      completePurchase();
      return;
    }

    // verify estimate tax didn't return an invalid address error
    if (giftPricingError?.toLowerCase() !== INVALID_ADDRESS) {
      completePurchase();
    } else {
      setShowAddressVerificationModal(true);
    }
  };

  return (
    <div className={styles.gifts}>
      <div className={headerClassNames}>
        <span {...setData(philterPage, "hero-intro")}>
          {strings.headerTextFirst}
        </span>
        <span {...setData(philterPage, "hero-site")}>
          {strings.toSite(currentSite.name)}
        </span>
        <div
          className={styles.subHeadline}
          {...setData(philterPage, "hero-close")}
        >
          {strings.headerTextLast}
        </div>
        <style jsx>{`
          .headerSection {
            background-image: linear-gradient(
                ${currentSite.brandingColorOverlay},
                ${currentSite.brandingColorOverlay}
              ),
              url("/static/sign_up_hero_bw_1024.jpg");
          }
        `}</style>
      </div>
      <GiftPurchaseStep
        currentSite={currentSite}
        formValues={formValues}
        giftPlans={giftPlans}
        handleInputBlur={handleInputBlur}
        handleInputChange={handleInputChange}
        handlePersonalNoteBlur={handlePersonalNoteBlur}
        handlePersonalNoteChange={handlePersonalNoteChange}
        handleSelectedSite={handleSelectedSite}
        handleSelectedSiteCleared={handleSelectedSiteCleared}
        onGiftPlanChange={(giftPlanName: string): void => {
          const giftPlan = find(
            giftPlans,
            plan => plan.displayName === giftPlanName
          );
          if (giftPlan) {
            dirty.current = true;
            setGiftPlan(giftPlan);
          }
        }}
        personalNote={personalNote}
        selectedGiftPlan={selectedGiftPlan}
      />
      <BillingInformationStep
        addressContainerRef={addressContainerRef}
        {...billingFormProps}
        isPayPalEnabled={
          !!formValues.gifterEmail &&
          isValidEmail(formValues.gifterEmail) &&
          payPalEnabledEmail === formValues.gifterEmail
        }
        payPalError={getPayPalError()}
        stepNumber={2}
        userEmail={formValues.gifterEmail}
      />
      {selectedSite && (
        <CompletePurchaseStep
          address={taxAddress}
          buttonDisabled={submitting || isCheckingOut || invalidInput || !valid}
          debitAgreedState={debitAgreedState}
          debitTerms={debitTerms}
          isAutoRenewAgreed={isAutoRenewAgreed}
          isDebit={billingFormProps.isDebit}
          payPalEmail={payPalEmail}
          promoCodeValue=""
          requiresAutoRenewalOptIn={requiresAutoRenewalOptIn}
          selectedPlan={selectedGiftPlan}
          selectedSite={selectedSite}
          setIsAutoRenewAgreed={setIsAutoRenewAgreed}
          stepNumber={3}
          submit={onPurchase}
          tax={giftPricing && giftPricing.tax}
        />
      )}
      {showAddressVerificationModal && (
        <AddressVerificationModal
          addressContainerRef={addressContainerRef}
          changeVisibility={setShowAddressVerificationModal}
          formAddress={{
            city: billingFormProps.billingFormState.city.value,
            state: billingFormProps.billingFormState.state.value,
            street1: billingFormProps.billingFormState.street1.value,
            street2: billingFormProps.billingFormState.street2.value,
            zip: billingFormProps.billingFormState.zip.value
          }}
          siteId={selectedSite?.id}
          smartyAuthToken={billingConstants.smartyStreetsWebToken}
          updateAddress={billingFormProps.updateAddress}
          visible={showAddressVerificationModal}
        />
      )}
    </div>
  );
};

export default Gifts;
